import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { GLItemKind, GLItemStatus, GLItemStatuses, weightedPotential } from '../../../DTO/GLItemDTO'
import {
  GLItemChartRow,
  GLItemsDTO,
  SalesChartGroupBy,
  SalesChartLayout,
  SalesChartListRow,
  SalesChartMode,
  SalesChartPoint,
  SalesChartSeries,
} from '../../../DTO/GLItemsDTO'
import mainservice from '../../../services/MainService'
import './SalesPotentialChart.scss'

export type SalesChartVariant = 'teaser' | 'full' | 'tile'

const euro = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const FRESHNESS_BAR_COLORS: Record<string, string> = {
  moldy: '#6d4c41',
  expired: '#ef6c00',
  overripe: '#f9a825',
  ripe: '#9ccc65',
  fresh: '#43a047',
}

const YEARLY_BAR_COLORS: Partial<Record<GLItemStatus, string>> = {
  [GLItemStatus.marketingOrderWon]: '#2e7d32',
  [GLItemStatus.marketingOrderLost]: '#c62828',
}

function marketingStatusOptions(allowed?: GLItemStatus[]): Array<{ value: string, title: string }> {
  return (GLItemStatuses[GLItemKind.marketing] || [])
    .filter(s => !(s as { hideInTabs?: boolean }).hideInTabs)
    .filter(s => {
      if (!allowed || allowed.length === 0) {
        return true
      }
      if (s.status === undefined) {
        return true
      }
      return allowed.indexOf(s.status) >= 0
    })
    .map(s => ({
      value: s.status === undefined ? 'all' : String(s.status),
      title: s.name,
    }))
}

function QuarterAxisTick(props: {
  x?: number
  y?: number
  payload?: { value: string }
  index?: number
  points?: SalesChartPoint[]
}) {
  const { x = 0, y = 0, index = 0, payload, points = [] } = props
  const point = points.find(p => p.xKey === payload?.value) || points[index]
  if (!point) {
    return null
  }
  const showYear = point.quarter === 'Q1' || index === 0 || (index > 0 && points[index - 1]?.year !== point.year)
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={14} textAnchor="middle" fontSize={11} fill="#444">{point.quarter}</text>
      {showYear && (
        <text dy={30} textAnchor="middle" fontSize={11} fontWeight={600} fill="#222">{point.year}</text>
      )}
    </g>
  )
}

function CategoryAxisTick(props: {
  x?: number
  y?: number
  payload?: { value: string }
  points?: SalesChartPoint[]
  rotate?: boolean
}) {
  const { x = 0, y = 0, payload, points = [], rotate } = props
  const point = points.find(p => p.xKey === payload?.value)
  const label = point?.label || payload?.value || ''
  if (rotate) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={8} dx={-2} textAnchor="end" fontSize={10} fill="#444" transform="rotate(-35)">{label}</text>
      </g>
    )
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={14} textAnchor="middle" fontSize={11} fill="#444">{label}</text>
    </g>
  )
}

const TOOLTIP_ITEM_LIMIT = 10

function barItemLabel(row: GLItemChartRow): string {
  const company = row.customerName || 'Ohne Firma'
  const project = row.headline || '(ohne Titel)'
  return `${company} – ${project}`
}

function SalesChartTooltip(props: {
  active?: boolean
  payload?: Array<{ dataKey?: string, value?: number, payload?: SalesChartPoint }>
  seriesByKey: Map<string, SalesChartSeries>
  mode: SalesChartMode
  groupBy: SalesChartGroupBy
  barItems?: GLItemChartRow[]
}) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null
  }
  const payload = props.payload.filter(p => p.dataKey !== 'total')
  if (payload.length === 0) {
    return null
  }
  const entry = payload.find(p => (p.value || 0) > 0) || payload[0]
  const point = entry.payload
  const dataKey = String(entry.dataKey || '')
  if (!point) {
    return null
  }

  let name = ''
  if (props.groupBy === 'quarter') {
    const series = props.seriesByKey.get(dataKey)
    if (!series) {
      return null
    }
    name = props.mode === 'compare'
      ? series.label
      : props.mode === 'company'
        ? series.customerName
        : `${series.customerName} – ${series.headline}`
  } else {
    const series = props.seriesByKey.get(dataKey)
    name = series?.label || point.label
  }

  const headline = props.groupBy === 'quarter' && point.year
    ? `${point.year} ${point.quarter || ''}`.trim()
    : point.label
  const barItems = (props.barItems || [])
    .slice()
    .sort((a, b) => {
      return weightedPotential(b.revenue_potential, b.revenue_probability)
        - weightedPotential(a.revenue_potential, a.revenue_probability)
    })
  const visibleItems = barItems.slice(0, TOOLTIP_ITEM_LIMIT)
  const hiddenCount = barItems.length - visibleItems.length

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: 1,
        padding: 1,
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        maxWidth: 320,
        maxHeight: 280,
        overflow: 'auto',
      }}
    >
      <Typography variant="body2" sx={{ color: '#666' }}>
        {headline}
      </Typography>
      {name && name !== headline && name !== 'Gewichtetes Potenzial' && (
        <Typography variant="body2">{name}</Typography>
      )}
      {visibleItems.map(row => (
        <Typography key={row.id} variant="body2">
          {barItemLabel(row)}
          {' · '}
          {euro.format(weightedPotential(row.revenue_potential, row.revenue_probability))}
        </Typography>
      ))}
      {hiddenCount > 0 && (
        <Typography variant="body2" sx={{ color: '#666' }}>
          und {hiddenCount} weitere
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {euro.format(entry.value || 0)}
      </Typography>
    </Box>
  )
}

export default function SalesPotentialChart(props: {
  mode?: SalesChartMode
  variant: SalesChartVariant
  kind?: GLItemKind
  title?: string
  statuses?: GLItemStatus[]
  initialStatus?: GLItemStatus
  groupBy?: SalesChartGroupBy
  layout?: SalesChartLayout
  yearCount?: number
  showTotalLine?: boolean
  dto?: GLItemsDTO
  loading?: boolean
  dataTick?: number
  barColor?: string
  onOpenFull?: () => void
  onLayoutChange?: (layout: SalesChartLayout) => void
}) {
  const kind = props.kind ?? GLItemKind.marketing
  const mode = props.mode ?? 'company'
  const groupBy = props.groupBy ?? 'quarter'
  const layout = props.layout ?? 'graph'
  const title = props.title ?? 'Gewichtetes Potenzial'
  const shared = !!props.dto
  const [ownDto] = useState(() => new GLItemsDTO({ kind }))
  const dto = props.dto ?? ownDto
  const [ownLoading, setOwnLoading] = useState(!shared)
  const [status, setStatus] = useState<GLItemStatus | undefined>(props.initialStatus)
  const [filterId, setFilterId] = useState('')
  const [barKey, setBarKey] = useState('')
  const [barSeriesKey, setBarSeriesKey] = useState('')
  const [hoverSeriesKey, setHoverSeriesKey] = useState('')
  const [ownTick, setOwnTick] = useState(0)
  const barListable = groupBy === 'year' || groupBy === 'freshness' || groupBy === 'wonLost'
  const listMode: SalesChartMode = mode === 'compare' ? 'project' : mode

  useEffect(() => {
    if (shared) {
      return
    }
    let cancelled = false
    async function load() {
      setOwnLoading(true)
      await dto.getChartFromServer({ kind })
      if (!cancelled) {
        setOwnLoading(false)
        setOwnTick(t => t + 1)
      }
    }
    load()
    return () => { cancelled = true }
  }, [dto, kind, shared])

  useEffect(() => {
    setFilterId('')
  }, [mode])

  useEffect(() => {
    setStatus(props.initialStatus)
  }, [props.initialStatus])

  useEffect(() => {
    setBarKey('')
    setBarSeriesKey('')
    setHoverSeriesKey('')
  }, [groupBy, mode, layout, props.statuses])

  const dataTick = shared ? (props.dataTick ?? 0) : ownTick
  const loading = shared ? (props.loading ?? false) : ownLoading

  const stack = useMemo(() => {
    void dataTick
    if (groupBy === 'year') {
      return dto.buildYearlyChart(props.statuses || [], 'value', props.yearCount)
    }
    if (groupBy === 'freshness') {
      return dto.buildFreshnessChart(props.statuses || [])
    }
    if (groupBy === 'wonLost') {
      return dto.buildWonLostChart(props.yearCount || 3)
    }
    if (mode === 'compare') {
      return dto.buildCompareChart(status !== undefined ? [status] : (props.statuses || []))
    }
    return dto.buildStackedChart(mode, status, filterId || undefined, props.statuses)
  }, [dto, mode, status, filterId, dataTick, groupBy, props.statuses, props.yearCount])

  const showingBarList = barListable && layout === 'graph' && !!barKey
  const showingPageList = layout === 'list' && (groupBy === 'quarter' || barListable)
  const showingList = showingPageList || showingBarList

  const listRows = useMemo(() => {
    void dataTick
    if (groupBy === 'quarter') {
      if (layout !== 'list') {
        return [] as SalesChartListRow[]
      }
      return dto.chartListRows(mode, status, filterId || undefined, props.statuses)
    }
    if (!barListable) {
      return [] as SalesChartListRow[]
    }
    if (layout !== 'list' && !barKey) {
      return [] as SalesChartListRow[]
    }
    return dto.chartListRowsForGroup(
      groupBy,
      props.statuses,
      listMode,
      layout === 'list' ? undefined : barKey,
      layout === 'list' ? undefined : (barSeriesKey || undefined),
      props.yearCount,
    )
  }, [dto, mode, listMode, status, filterId, dataTick, groupBy, layout, props.statuses, props.yearCount, barListable, barKey, barSeriesKey])

  const seriesByKey = useMemo(() => {
    const map = new Map<string, SalesChartSeries>()
    stack.series.forEach(s => map.set(s.key, s))
    return map
  }, [stack.series])

  const hasBars = stack.series.length > 0
  const height = props.variant === 'full' ? 420 : props.variant === 'tile' ? 220 : 260
  const showFilters = props.variant === 'full' && groupBy === 'quarter'
  const stackedBars = groupBy === 'quarter' && mode !== 'compare'

  function openFull() {
    if (props.onOpenFull) {
      props.onOpenFull()
      return
    }
    mainservice.navTo([
      ['view', 'spMarketingChart'],
      ['status', 'all'],
      ['chartMode', mode],
    ])
  }

  function changeStatus(raw: string) {
    const next = raw === 'all' ? undefined : parseInt(raw, 10)
    setStatus(Number.isNaN(next as number) ? undefined : next)
    setFilterId('')
    if (props.variant === 'full') {
      mainservice.nav.setVal('status', raw)
    }
  }

  function jumpToListRow(row: SalesChartListRow) {
    if ((row.itemCount || 1) > 1) {
      return
    }
    mainservice.navTo([
      ['view', 'spMarketing'],
      ['status', String(row.status)],
      ['id', row.id],
    ])
  }

  function openBarList(xKey: string, seriesKey: string, value: number) {
    if (!barListable || value <= 0) {
      return
    }
    setBarKey(xKey)
    setBarSeriesKey(seriesKey)
  }

  function closeBarList() {
    setBarKey('')
    setBarSeriesKey('')
  }

  function barItemsForTooltip(xKey?: string, seriesKey?: string): GLItemChartRow[] {
    if (!barListable || !xKey || seriesKey === 'total') {
      return []
    }
    return dto.chartItemsForBar({
      groupBy,
      statuses: props.statuses,
      xKey,
      seriesKey,
      yearCount: props.yearCount,
    })
  }

  function barListTitle(): string {
    const point = stack.points.find(p => p.xKey === barKey)
    const series = seriesByKey.get(barSeriesKey)
    const label = point?.label || barKey
    if (series?.label && series.label !== 'Gewichtetes Potenzial') {
      return `${title}: ${series.label} ${label}`
    }
    return `${title}: ${label}`
  }

  function jumpToProject(series: SalesChartSeries) {
    mainservice.navTo([
      ['view', 'spMarketing'],
      ['status', String(series.status)],
      ['id', series.itemId],
    ])
  }

  function jumpToCompany(series: SalesChartSeries) {
    if (!series.customerId) {
      return
    }
    mainservice.navTo([
      ['view', 'spCustomerEdit'],
      ['id', series.customerId],
    ])
  }

  function jumpToSeries(series: SalesChartSeries) {
    if (groupBy !== 'quarter') {
      return
    }
    if (mode === 'project') {
      jumpToProject(series)
      return
    }
    if (mode === 'company') {
      jumpToCompany(series)
    }
  }

  function statusLabel(statusValue: GLItemStatus): string {
    const entry = marketingStatusOptions().find(s => s.value === String(statusValue))
    return entry?.title || `${statusValue}`
  }

  const statusOptions = marketingStatusOptions(props.statuses)
  const entityLabel = mode === 'company' ? 'Firma' : 'Projekt'
  const yearlyStatus = props.statuses && props.statuses.length === 1 ? props.statuses[0] : undefined
  const yearlyColor = props.barColor
    || (yearlyStatus !== undefined ? YEARLY_BAR_COLORS[yearlyStatus] : undefined)

  function emptyMessage(): string {
    if (groupBy === 'year' || groupBy === 'wonLost') {
      return 'Keine Einträge mit gewichtetem Potenzial'
    }
    if (groupBy === 'freshness') {
      return 'Keine Einträge mit Datum und gewichtetem Potenzial'
    }
    return 'Keine Einträge mit Quartal und gewichtetem Potenzial'
  }

  function barFill(series: SalesChartSeries, xKey: string): string {
    if (groupBy === 'freshness') {
      return FRESHNESS_BAR_COLORS[xKey] || series.color
    }
    if (groupBy === 'year') {
      return yearlyColor || series.color
    }
    return series.color
  }

  const xAxisHeight = groupBy === 'quarter' ? 40 : groupBy === 'freshness' ? 52 : 28
  const titleVariant = props.variant === 'tile' ? 'h6' : 'h5'

  return (
    <div className={`SalesPotentialChart SalesPotentialChart-${props.variant}`}>
      <div className="SalesPotentialChart-header">
        <Typography
          variant={titleVariant}
          component="h3"
          className="SalesPotentialChart-title"
          onClick={props.variant === 'teaser' ? openFull : undefined}
        >
          {showingBarList ? barListTitle() : title}
        </Typography>
        {props.variant === 'teaser' && (
          <Button size="small" onClick={openFull} sx={{ textTransform: 'none' }}>Öffnen</Button>
        )}
        {showingBarList && (
          <Button size="small" onClick={closeBarList} sx={{ textTransform: 'none' }}>
            Zurück zur Graphik
          </Button>
        )}
        {props.variant === 'full' && groupBy === 'quarter' && props.onLayoutChange && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={layout === 'graph' ? 'contained' : 'outlined'}
              onClick={() => props.onLayoutChange && props.onLayoutChange('graph')}
              sx={{ textTransform: 'none' }}
            >
              Graph
            </Button>
            <Button
              size="small"
              variant={layout === 'list' ? 'contained' : 'outlined'}
              onClick={() => props.onLayoutChange && props.onLayoutChange('list')}
              sx={{ textTransform: 'none' }}
            >
              Liste
            </Button>
          </Stack>
        )}
      </div>

      {showFilters && (
        <div className="SalesPotentialChart-filters">
          <div className="SalesPotentialChart-filter">
            <label htmlFor="sales-chart-status">Status</label>
            <select
              id="sales-chart-status"
              value={status === undefined ? 'all' : String(status)}
              onChange={e => changeStatus(e.target.value)}
            >
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>{o.title}</option>
              ))}
            </select>
          </div>
          {mode !== 'compare' && (
          <div className="SalesPotentialChart-filter">
            <label htmlFor="sales-chart-entity">{entityLabel}</label>
            <select
              id="sales-chart-entity"
              value={filterId}
              onChange={e => setFilterId(e.target.value)}
            >
              <option value="">Alle</option>
              {stack.filterOptions.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      )}

      {loading && <div className="SalesPotentialChart-loading">Lade…</div>}
      {!loading && showingList && listRows.length === 0 && (
        <div className="SalesPotentialChart-empty">
          {emptyMessage()}
        </div>
      )}
      {!loading && !showingList && !hasBars && (
        <div className="SalesPotentialChart-empty">
          {emptyMessage()}
        </div>
      )}

      {!loading && showingList && listRows.length > 0 && (
        <div className="SalesPotentialChart-list">
          <table>
            <thead>
              {groupBy === 'quarter' ? (
                <tr>
                  <th>Titel</th>
                  <th>Firma</th>
                  <th>Person</th>
                  <th>Status</th>
                  <th>Quartal</th>
                  <th>Gewichtetes Potenzial</th>
                </tr>
              ) : (
                <tr>
                  <th>{listMode === 'company' ? 'Firma' : 'Titel'}</th>
                  {listMode === 'company' && <th>Positionen</th>}
                  {listMode !== 'company' && <th>Firma</th>}
                  <th>{groupBy === 'freshness' ? 'Frische' : 'Jahr'}</th>
                  <th>Gewichtetes Potenzial</th>
                </tr>
              )}
            </thead>
            <tbody>
              {groupBy === 'quarter' && listRows.map(row => (
                <tr key={row.id} onClick={() => jumpToListRow(row)}>
                  <td>{row.headline}</td>
                  <td>{row.customerName}</td>
                  <td>{row.personName}</td>
                  <td>{statusLabel(row.status)}</td>
                  <td>{row.quarterLabel}</td>
                  <td>{euro.format(row.value)}</td>
                </tr>
              ))}
              {groupBy !== 'quarter' && listRows.map(row => (
                <tr
                  key={listMode === 'company' ? (row.groupKey || String(row.id)) : String(row.id)}
                  className={(row.itemCount || 1) > 1 ? 'is-grouped' : undefined}
                  onClick={() => jumpToListRow(row)}
                >
                  <td>{listMode === 'company' ? row.customerName : row.headline}</td>
                  {listMode === 'company' && <td>{row.itemCount || 1}</td>}
                  {listMode !== 'company' && <td>{row.customerName}</td>}
                  <td>{row.quarterLabel}</td>
                  <td>{euro.format(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !showingList && hasBars && (
        <div className="SalesPotentialChart-body">
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={stack.points} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="xKey"
                interval={0}
                height={xAxisHeight}
                tick={groupBy === 'quarter'
                  ? <QuarterAxisTick points={stack.points} />
                  : <CategoryAxisTick points={stack.points} rotate={groupBy === 'freshness'} />
                }
              />
              <YAxis
                tickFormatter={(v: number) => euro.format(v)}
                width={88}
              />
              <Tooltip
                shared={false}
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={(tipProps) => {
                  const payload = ((tipProps.payload || []) as Array<{ dataKey?: string, value?: number, payload?: SalesChartPoint }>)
                    .filter(p => p.dataKey !== 'total')
                  const hovered = hoverSeriesKey
                    ? payload.find(p => p.dataKey === hoverSeriesKey)
                    : undefined
                  const entry = hovered || payload.find(p => (p.value || 0) > 0) || payload[0]
                  return (
                    <SalesChartTooltip
                      active={tipProps.active}
                      payload={entry ? [entry] : []}
                      seriesByKey={seriesByKey}
                      mode={mode}
                      groupBy={groupBy}
                      barItems={barItemsForTooltip(entry?.payload?.xKey, String(entry?.dataKey || ''))}
                    />
                  )
                }}
              />
              {stack.series.map(s => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId={stackedBars ? 'potential' : undefined}
                  fill={barFill(s, s.key)}
                  maxBarSize={48}
                  cursor={barListable || (groupBy === 'quarter' && mode !== 'compare') ? 'pointer' : 'default'}
                  onMouseOver={() => {
                    if (stackedBars) {
                      setHoverSeriesKey(s.key)
                    }
                  }}
                  onMouseOut={() => {
                    setHoverSeriesKey(current => current === s.key ? '' : current)
                  }}
                  onClick={(data) => {
                    const point = (data && (data as { payload?: SalesChartPoint }).payload) || data
                    const xKey = point && (point as { xKey?: string }).xKey
                    const value = point ? Number((point as Record<string, unknown>)[s.key]) || 0 : 0
                    if (barListable) {
                      openBarList(xKey || '', s.key, value)
                      return
                    }
                    if (value > 0) {
                      jumpToSeries(s)
                    }
                  }}
                >
                  {groupBy !== 'quarter' && stack.points.map(point => (
                    <Cell
                      key={`${s.key}-${point.xKey}`}
                      fill={barFill(s, point.xKey)}
                      cursor={(Number(point[s.key]) || 0) > 0 ? 'pointer' : 'default'}
                      onClick={() => openBarList(point.xKey, s.key, Number(point[s.key]) || 0)}
                    />
                  ))}
                </Bar>
              ))}
              {props.showTotalLine && stackedBars && (
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Summe"
                  stroke="#66bb6a"
                  strokeWidth={2}
                  dot={false}
                  legendType="none"
                  tooltipType="none"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {showFilters && mode !== 'compare' && hasBars && (
        <div className="SalesPotentialChart-hint">
          {mode === 'company'
            ? 'Klick auf ein Segment öffnet die Firma.'
            : 'Klick auf ein Segment öffnet die zugehörige Vertriebskarte.'}
        </div>
      )}
      {barListable && !showingList && hasBars && (
        <div className="SalesPotentialChart-hint">
          Klick auf einen Balken zeigt die Positionen.
        </div>
      )}
    </div>
  )
}

export function SalesPotentialModeToggle(props: {
  mode: SalesChartMode
  onChange: (mode: SalesChartMode) => void
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }}>
      <Button
        variant={props.mode === 'company' ? 'contained' : 'outlined'}
        onClick={() => props.onChange('company')}
        sx={{ textTransform: 'none' }}
      >
        Firmen
      </Button>
      <Button
        variant={props.mode === 'project' ? 'contained' : 'outlined'}
        onClick={() => props.onChange('project')}
        sx={{ textTransform: 'none' }}
      >
        Projekte
      </Button>
      <Button
        variant={props.mode === 'compare' ? 'contained' : 'outlined'}
        onClick={() => props.onChange('compare')}
        sx={{ textTransform: 'none' }}
      >
        Vergleich
      </Button>
    </Stack>
  )
}